import React from 'react'
import EChartCustomized from '@/components/EchartCustomized'
import type { EChartsOption, BarSeriesOption } from 'echarts'

// ECharts animation easing types
type AnimationEasingType =
    | 'linear'
    | 'quadraticIn'
    | 'quadraticOut'
    | 'quadraticInOut'
    | 'cubicIn'
    | 'cubicOut'
    | 'cubicInOut'
    | 'quarticIn'
    | 'quarticOut'
    | 'quarticInOut'
    | 'quinticIn'
    | 'quinticOut'
    | 'quinticInOut'
    | 'sinusoidalIn'
    | 'sinusoidalOut'
    | 'sinusoidalInOut'
    | 'exponentialIn'
    | 'exponentialOut'
    | 'exponentialInOut'
    | 'circularIn'
    | 'circularOut'
    | 'circularInOut'
    | 'elasticIn'
    | 'elasticOut'
    | 'elasticInOut'
    | 'backIn'
    | 'backOut'
    | 'backInOut'
    | 'bounceIn'
    | 'bounceOut'
    | 'bounceInOut'

export interface BarChartDataItem {
    /** Category label for the x-axis */
    category: string
    /** Values for each series (in order of series array) */
    values: number[]
    [key: string]: unknown
}

export interface BarChartSeries {
    /** Name of the series (displayed in legend and tooltip) */
    name: string
    /** Color for the series bars */
    color?: string
    /** Stack group name. Bars with same stack name will be stacked */
    stack?: string
    /** Bar width (number for pixels, or string like '50%') */
    barWidth?: number | string
    /** Maximum bar width */
    barMaxWidth?: number | string
    /** Gap between bars of different series (percentage string like '30%') */
    barGap?: string
    /** Gap between bar categories (percentage string like '20%') */
    barCategoryGap?: string
    /** Custom series options to override defaults */
    seriesOptions?: Partial<BarSeriesOption>
}

export interface BarChartProps {
    /** Data items to display in the chart */
    data: BarChartDataItem[]
    /** Series configuration (one for each group of bars) */
    series: BarChartSeries[]
    /** Height of the chart container */
    height?: number
    /** Width of the chart container */
    width?: number | string

    // Title options
    /** Title of the chart */
    title?: string
    /** Subtitle of the chart */
    subtitle?: string
    /** Title position */
    titlePosition?: 'left' | 'center' | 'right'

    // Legend options
    /** Whether to show the legend */
    showLegend?: boolean
    /** Legend orientation */
    legendOrient?: 'vertical' | 'horizontal'
    /** Legend position */
    legendPosition?: 'left' | 'right' | 'top' | 'bottom'
    /** Legend icon shape */
    legendIcon?:
        | 'circle'
        | 'rect'
        | 'roundRect'
        | 'triangle'
        | 'diamond'
        | 'pin'
        | 'arrow'
        | 'none'
    /** Legend icon size (width and height in pixels) */
    legendItemSize?: number
    /** Gap between the chart and legend in pixels */
    legendGap?: number

    // Axis options
    /** Whether to show the x-axis */
    showXAxis?: boolean
    /** X-axis label rotation angle (in degrees) */
    xAxisLabelRotate?: number
    /** Whether to show the y-axis */
    showYAxis?: boolean
    /** Y-axis name/label */
    yAxisName?: string
    /** Y-axis name position */
    yAxisNamePosition?: 'start' | 'middle' | 'end'
    /** Custom Y-axis formatter function */
    yAxisFormatter?: string | ((value: number) => string)
    /** Y-axis minimum value (undefined for auto) */
    yAxisMin?: number
    /** Y-axis maximum value (undefined for auto) */
    yAxisMax?: number
    /** Whether to show grid lines */
    showGridLines?: boolean

    // Tooltip options
    /** Whether to show tooltip on hover */
    showTooltip?: boolean
    /** Tooltip trigger type */
    tooltipTrigger?: 'item' | 'axis'
    /** Custom tooltip formatter function */
    tooltipFormatter?: (
        params: {
            seriesName: string
            name: string
            value: number
            color: string
            dataIndex: number
            seriesIndex: number
        }[]
    ) => string

    // Bar options
    /** Whether bars should be horizontal */
    horizontal?: boolean
    /** Default bar width for all series */
    barWidth?: number | string
    /** Maximum bar width for all series */
    barMaxWidth?: number | string
    /** Gap between bars of different series */
    barGap?: string
    /** Gap between bar categories */
    barCategoryGap?: string
    /** Border radius for bars [topLeft, topRight, bottomRight, bottomLeft] or single value */
    barBorderRadius?: number | [number, number, number, number]
    /** Whether to show value labels on bars */
    showBarLabel?: boolean
    /** Bar label position */
    barLabelPosition?: 'top' | 'inside' | 'insideTop' | 'insideBottom'
    /** Custom bar label formatter */
    barLabelFormatter?:
        | string
        | ((params: { value: number; name: string }) => string)

    // Grid/Layout options
    /** Grid left padding */
    gridLeft?: number | string
    /** Grid right padding */
    gridRight?: number | string
    /** Grid top padding */
    gridTop?: number | string
    /** Grid bottom padding */
    gridBottom?: number | string
    /** Whether grid should contain axis labels */
    gridContainLabel?: boolean

    // Animation options
    /** Whether to enable animation */
    animation?: boolean
    /** Animation duration in ms */
    animationDuration?: number
    /** Animation easing function */
    animationEasing?: AnimationEasingType

    /** Custom class name for the container */
    className?: string
    /** Custom inline styles for the container */
    style?: React.CSSProperties
}

const BarChart: React.FC<BarChartProps> = ({
    data,
    series,
    height = 300,
    width = '100%',

    // Title
    title,
    subtitle,
    titlePosition = 'left',

    // Legend
    showLegend = true,
    legendOrient = 'horizontal' as const,
    legendPosition = 'bottom',
    legendIcon = 'rect',
    legendItemSize = 8,
    legendGap = 0,

    // Axes
    showXAxis = true,
    xAxisLabelRotate = 0,
    showYAxis = true,
    yAxisName,
    yAxisNamePosition = 'end' as const,
    yAxisFormatter,
    yAxisMin,
    yAxisMax,
    showGridLines = true,

    // Tooltip
    showTooltip = true,
    tooltipTrigger = 'axis' as const,
    tooltipFormatter,

    // Bars
    horizontal = false,
    barWidth,
    barMaxWidth,
    barGap = '10%',
    barCategoryGap = '30%',
    barBorderRadius = 0,
    showBarLabel = false,
    barLabelPosition = 'top',
    barLabelFormatter,

    // Grid
    gridLeft = '3%',
    gridRight = '4%',
    gridTop = 60,
    gridBottom = 60,
    gridContainLabel = true,

    // Animation
    animation = true,
    animationDuration = 500,
    animationEasing = 'cubicOut' as const,

    className = '',
    style,
}) => {
    // Extract categories from data
    const categories = data.map((item) => item.category)

    // Get legend position config
    const getLegendPositionConfig = () => {
        switch (legendPosition) {
            case 'left':
                return { left: legendGap, top: 'center' }
            case 'right':
                return { right: legendGap, top: 'center' }
            case 'top':
                return { top: legendGap, left: 'center' }
            case 'bottom':
                return { bottom: legendGap, left: 'center' }
            default:
                return { bottom: legendGap, left: 'center' }
        }
    }

    // Build series data
    const buildSeriesData = (): BarSeriesOption[] => {
        return series.map((s, seriesIndex) => {
            const seriesData = data.map((item) => item.values[seriesIndex] ?? 0)

            const baseRadius = Array.isArray(barBorderRadius)
                ? barBorderRadius
                : [
                      barBorderRadius,
                      barBorderRadius,
                      barBorderRadius,
                      barBorderRadius,
                  ]

            return {
                type: 'bar' as const,
                name: s.name,
                data: seriesData,
                stack: s.stack,
                barWidth: s.barWidth ?? barWidth,
                barMaxWidth: s.barMaxWidth ?? barMaxWidth,
                barGap: s.barGap ?? barGap,
                barCategoryGap: s.barCategoryGap ?? barCategoryGap,
                itemStyle: {
                    color: s.color,
                    borderRadius: baseRadius,
                },
                label: {
                    show: showBarLabel,
                    position: barLabelPosition,
                    formatter:
                        typeof barLabelFormatter === 'function'
                            ? (params: unknown) => {
                                  const p = params as {
                                      value: number
                                      name: string
                                  }
                                  return barLabelFormatter({
                                      value: p.value,
                                      name: p.name,
                                  })
                              }
                            : (barLabelFormatter ?? '{c}'),
                },
                emphasis: {
                    focus: 'series',
                },
                ...s.seriesOptions,
            }
        })
    }

    // Build ECharts options
    const options: EChartsOption = {
        title: title
            ? {
                  text: title,
                  subtext: subtitle,
                  left: titlePosition,
                  textStyle: {
                      fontSize: 16,
                      fontWeight: 600,
                      color: '#1A237E',
                  },
                  subtextStyle: {
                      fontSize: 12,
                      color: '#666',
                  },
              }
            : undefined,

        tooltip: showTooltip
            ? {
                  trigger: tooltipTrigger,
                  axisPointer: {
                      type: 'shadow',
                  },
                  formatter: tooltipFormatter
                      ? (params: unknown) => {
                            const p = params as Array<{
                                seriesName: string
                                name: string
                                value: number
                                color: string
                                dataIndex: number
                                seriesIndex: number
                            }>
                            return tooltipFormatter(
                                p.map((item) => ({
                                    seriesName: item.seriesName,
                                    name: item.name,
                                    value: item.value,
                                    color: item.color,
                                    dataIndex: item.dataIndex,
                                    seriesIndex: item.seriesIndex,
                                }))
                            )
                        }
                      : undefined,
              }
            : undefined,

        legend: showLegend
            ? {
                  type: 'plain',
                  orient: legendOrient,
                  icon: legendIcon,
                  itemWidth: legendItemSize,
                  itemHeight: legendItemSize,
                  itemGap: 16,
                  textStyle: {
                      fontSize: 12,
                      color: 'rgba(0,0,0,0.7)',
                  },
                  ...getLegendPositionConfig(),
              }
            : undefined,

        grid: {
            left: gridLeft,
            right: gridRight,
            top: gridTop,
            bottom: gridBottom,
            containLabel: gridContainLabel,
        },

        xAxis: {
            type: horizontal ? 'value' : 'category',
            data: horizontal ? undefined : categories,
            show: showXAxis,
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#E0E0E0',
                },
            },
            axisTick: {
                show: false,
            },
            axisLabel: {
                rotate: xAxisLabelRotate,
                color: 'rgba(0,0,0,0.6)',
                fontSize: 12,
                formatter:
                    horizontal && yAxisFormatter
                        ? typeof yAxisFormatter === 'function'
                            ? (value: number) => yAxisFormatter(value)
                            : yAxisFormatter
                        : undefined,
            },
            splitLine: {
                show: horizontal && showGridLines,
                lineStyle: {
                    color: '#F0F0F0',
                    type: 'dashed',
                },
            },
            min: horizontal ? yAxisMin : undefined,
            max: horizontal ? yAxisMax : undefined,
        },

        yAxis: {
            type: horizontal ? 'category' : 'value',
            data: horizontal ? categories : undefined,
            show: showYAxis,
            name: yAxisName,
            nameLocation: yAxisNamePosition,
            nameTextStyle: {
                color: 'rgba(0,0,0,0.6)',
                fontSize: 12,
            },
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLabel: {
                color: 'rgba(0,0,0,0.6)',
                fontSize: 12,
                formatter:
                    !horizontal && yAxisFormatter
                        ? typeof yAxisFormatter === 'function'
                            ? (value: number) => yAxisFormatter(value)
                            : yAxisFormatter
                        : undefined,
            },
            splitLine: {
                show: !horizontal && showGridLines,
                lineStyle: {
                    color: '#F0F0F0',
                    type: 'dashed',
                },
            },
            min: !horizontal ? yAxisMin : undefined,
            max: !horizontal ? yAxisMax : undefined,
        },

        series: buildSeriesData(),

        animation,
        animationDuration,
        animationEasing: animationEasing as AnimationEasingType,
    }

    return (
        <div className={className} style={style}>
            <EChartCustomized
                options={options}
                style={{ height, width }}
                opts={{ renderer: 'svg' }}
            />
        </div>
    )
}

export default BarChart
